import type { Store } from '@types'
import { mergeSnapshotIntoState } from './applySnapshotToDraft'
import { clearHistoryBatchState } from './historyRecordingState'
import { runPostHistoryChange } from './postHistoryChange'
import {
  clearHistoryStacks,
  getHistoryFutureCount,
  getHistoryPastCount,
  popHistoryFutureSnapshot,
  popHistoryPastSnapshot,
  pushHistoryFutureSnapshot,
  pushHistoryPastSnapshot,
  syncHistoryMetaFromStacks,
} from './historyStack'
import {
  captureHistorySnapshot,
  deserializeHistorySnapshot,
  serializeHistorySnapshot,
} from './snapshot'
import { hasSceneHistoryDiff } from './shouldRecordHistoryChange'
import { wiredHistoryStoreRef, type WiredHistoryStore } from './wiredStoreRef'

let isApplyingHistory = false
let isPaused = false
let pausedBaseline: string | null = null

function getWiredStore(): WiredHistoryStore | null {
  return wiredHistoryStoreRef.current
}

export function wireHistoryStore(store: WiredHistoryStore): void {
  wiredHistoryStoreRef.current = store
}

export function isHistoryApplyInProgress(): boolean {
  return isApplyingHistory
}

export function commitHistoryBatchSnapshot(beforeSerialized: string): void {
  const wiredStore = getWiredStore()
  if (!wiredStore) return

  const before = deserializeHistorySnapshot(beforeSerialized)
  const after = captureHistorySnapshot(wiredStore.getState())
  if (!hasSceneHistoryDiff(before, after)) return

  pushHistoryPastSnapshot(beforeSerialized)
}

export function clearHistoryEngine(): void {
  pausedBaseline = null
  isPaused = false
  clearHistoryBatchState()
  clearHistoryStacks()
}

function getStoreState(): Store {
  const wiredStore = getWiredStore()
  if (!wiredStore) {
    throw new Error('[history] Store no enlazado. Llama a wireHistoryStore tras crear el store.')
  }
  return wiredStore.getState()
}

function applySerializedSnapshot(serialized: string): void {
  const wiredStore = getWiredStore()
  if (!wiredStore) return

  const snapshot = deserializeHistorySnapshot(serialized)
  const current = wiredStore.getState()

  isApplyingHistory = true
  try {
    wiredStore.setState(mergeSnapshotIntoState(current, snapshot), true)
  } finally {
    isApplyingHistory = false
  }
}

function bumpRenderCounter(): void {
  const wiredStore = getWiredStore()
  if (!wiredStore) return

  isApplyingHistory = true
  try {
    wiredStore.setState((draft) => {
      draft.contadorModificaciones = (draft.contadorModificaciones || 0) + 1
      draft.sceneHistoryRevision = (draft.sceneHistoryRevision || 0) + 1
    })
  } finally {
    isApplyingHistory = false
  }
}

export function pauseHistoryEngine(): void {
  if (!getWiredStore() || isPaused) return
  isPaused = true
  pausedBaseline = serializeHistorySnapshot(captureHistorySnapshot(getStoreState()))
}

export function resumeHistoryEngine(recordChange: (beforeSerialized: string) => void): void {
  if (!isPaused) return
  isPaused = false
  if (pausedBaseline) {
    recordChange(pausedBaseline)
    pausedBaseline = null
  }
}

export function undoHistory(): void {
  const wiredStore = getWiredStore()
  if (!wiredStore) {
    if (import.meta.env.DEV) {
      console.warn('[history] undo ignorado: store no enlazado')
    }
    return
  }
  if (isApplyingHistory) return

  if (getHistoryPastCount() === 0) {
    syncHistoryMetaFromStacks()
    return
  }

  const currentSerialized = serializeHistorySnapshot(captureHistorySnapshot(wiredStore.getState()))
  const previousSerialized = popHistoryPastSnapshot()
  if (!previousSerialized) {
    syncHistoryMetaFromStacks()
    return
  }

  pushHistoryFutureSnapshot(currentSerialized)
  applySerializedSnapshot(previousSerialized)
  syncHistoryMetaFromStacks()
  runPostHistoryChange(() => wiredStore.getState(), bumpRenderCounter)
}

export function redoHistory(): void {
  const wiredStore = getWiredStore()
  if (!wiredStore || isApplyingHistory) return

  if (getHistoryFutureCount() === 0) {
    syncHistoryMetaFromStacks()
    return
  }

  const currentSerialized = serializeHistorySnapshot(captureHistorySnapshot(wiredStore.getState()))
  const nextSerialized = popHistoryFutureSnapshot()
  if (!nextSerialized) {
    syncHistoryMetaFromStacks()
    return
  }

  pushHistoryPastSnapshot(currentSerialized)
  applySerializedSnapshot(nextSerialized)
  syncHistoryMetaFromStacks()
  runPostHistoryChange(() => wiredStore.getState(), bumpRenderCounter)
}

export function recordDestructiveHistoryChange(runChange: () => void): void {
  const wiredStore = getWiredStore()
  if (!wiredStore) {
    runChange()
    return
  }

  if (isApplyingHistory || isPaused) {
    runChange()
    return
  }

  const beforeSerialized = serializeHistorySnapshot(captureHistorySnapshot(wiredStore.getState()))

  runChange()

  const before = deserializeHistorySnapshot(beforeSerialized)
  const after = captureHistorySnapshot(wiredStore.getState())
  if (!hasSceneHistoryDiff(before, after)) return

  pushHistoryPastSnapshot(beforeSerialized)
  syncHistoryMetaFromStacks()
}

export function isHistoryPaused(): boolean {
  return isPaused
}
